// src/utils/dateHelper.js

// Helper function to format dates as YYYY-MM-DD
export function formatDate(date) {
  const d = new Date(date);
  let month = '' + (d.getMonth() + 1);
  let day = '' + d.getDate();
  const year = d.getFullYear();

  if (month.length < 2) month = '0' + month;
  if (day.length < 2) day = '0' + day;

  return [year, month, day].join('-');
}

// Today's date
const today = new Date();
const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

// Yesterday's date
const yesterday = new Date(today);
yesterday.setDate(yesterday.getDate() - 1);

// Last 7 days start date
const last7Days = new Date(today);
last7Days.setDate(last7Days.getDate() - 6);

// Calculate the first and last day of the previous month
const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0); // Last day of the previous month

export function getFormattedLoggedInDate(dateStr) {
  const date = new Date(dateStr);

  const options = {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
  };
  return date.toLocaleString('en-US', options).replace(',', ' at');
}

export function getFormattedDate(dateStr) {
  const date = new Date(dateStr);
  
  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return '';
  }
  
  // Get today and yesterday dates for comparison
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  
  // Reset time portion for accurate date comparison
  const normalizedToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const normalizedYesterday = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
  const normalizedInput = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  
  // Format time portion
  const timeOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  };
  const timeString = date.toLocaleTimeString('en-US', timeOptions);
  
  // Compare dates and return appropriate string
  if (normalizedInput.getTime() === normalizedToday.getTime()) {
    return `Today at ${timeString}`;
  } else if (normalizedInput.getTime() === normalizedYesterday.getTime()) {
    return `Yesterday at ${timeString}`;
  } else {
    // Use the original formatting for other dates
    const options = {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    };
    return date.toLocaleString('en-US', options).replace(',', ' at');
  }
}

export function getMonthsForYear(year = new Date().getFullYear()) {
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June', 
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentMonth = new Date().getMonth(); // Get current month (0-11)
  const months = [];

  for (let i = 0; i < 12; i++) {
    // Use 2024 for any month after the current month
    const monthYear = i > currentMonth ? 2024 : year;
    
    const start = new Date(monthYear, i, 1, 2, 0, 0); // Start date of the month at 02:00:00
    const end = new Date(monthYear, i + 1, 0, 23, 59, 59); // End date of the month (last day at 23:59:59)
    months.push({
      label: monthNames[i],
      start,
      end
    });
  }
  return months;
}

export function getMonthName(monthNumber) {
    const date = new Date(1970, monthNumber, 1); //year is irrelevant
    return date.toLocaleString('default', { month: 'long' });
};

// Helper to get the current month index
export const getCurrentMonthIndex = () => {
  const currentDate = new Date();
  return currentDate.getMonth();
};

export const getCurrentMonth = () => {
  const currentDate = new Date();
  return currentDate.getMonth();
};

export const getFirstDayOfCurrentMonth = () => {
  return new Date(new Date().getFullYear(), getCurrentMonth(), 1);
};

export const trimMonthName = (monthName) => {
  return monthName.length > 6 ? monthName.slice(0, 3) : monthName;
};

export const formatMonthYear = (date) => {
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

export const getMonthAndYear = (date) => {
  const parsedDate = new Date(date);
  const month = parsedDate.getMonth() + 1; // Months are 0-based in JS, so we add 1 to get 1-12
  const monthName = parsedDate.toLocaleString('default', { month: 'long' }); // Get full month name
  const year = parsedDate.getFullYear();
  return { month, monthName, year };
};

export const getHRMSTime = (date) => {
  const parsedDate = new Date(date);
  return parsedDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
};

export const formatDateTime = (isoString) => {
  const date = new Date(isoString);
  return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
  }).format(date);
};

// Export date presets with actual Date objects
export const presets = [
  { 
    name: 'Today', 
    from: today, 
    to: today,
    incomeAlias: "Today's Income"
  },
  { 
    name: 'This month', 
    from: firstDayOfMonth, 
    to: today,
    incomeAlias: `${formatMonthYear(today).split(' ')[0]} Income` // Gets just the month name
  },
  { 
    name: 'Last Month', 
    from: firstDayLastMonth, 
    to: lastDayLastMonth,
    incomeAlias: `${formatMonthYear(firstDayLastMonth).split(' ')[0]} Income`
  },
  { 
    name: 'Last 2 days', 
    from: yesterday, 
    to: today,
    incomeAlias: "Income"
  },
  { 
    name: 'Last 7 days', 
    from: last7Days, 
    to: today,
    incomeAlias: "Income"
  }
];

export function getLastFourMonths() {
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  const months = [];
  
  // Get last 4 months (including current)
  for (let i = 3; i >= 0; i--) {
    let monthIndex = currentMonth - i;
    let year = currentYear;
    
    // Handle previous year's months
    if (monthIndex < 0) {
      monthIndex += 12;
      year -= 1;
    }
    console.log(getMonthName(monthIndex), monthIndex);
    const start = new Date(year, monthIndex, 1, 2, 0, 0);
    const end = new Date(year, monthIndex + 1, 0, 23, 59, 59);
    months.push({
      label: getMonthName(monthIndex),
      start,
      end
    });
  }
  console.log(months);
  return months;
}

// ... existing code ...

export function findCurrentMonthIndex(monthsArray) {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  
  const index = monthsArray.findIndex(month => {
    const monthDate = month.start;
    return monthDate.getMonth() === currentMonth && 
           monthDate.getFullYear() === currentYear;
  });
  
  // Return last index if current month not found
  return index === -1 ? monthsArray.length - 1 : index;
}

export function formatDateOnly(date) {
    if (!date) return '';
    const d = new Date(date);
    // Example: YYYY-MM-DD
    return d.toISOString().slice(0, 10);
    // Or for DD/MM/YYYY:
    // return d.toLocaleDateString('en-GB');
}